import * as React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Outlet, Link, createRootRouteWithContext, useRouter, HeadContent, Scripts } from "@tanstack/react-router";
import appCss from "../styles.css?url";
import { AuthProvider } from "@/lib/auth-context";
import { ThemeProvider } from "@/lib/theme-context";
import { I18nProvider } from "@/lib/i18n";
import { Toaster } from "@/components/ui/sonner";
import { FeedbackPrompt } from "@/components/FeedbackPrompt";
import { ProfileCompletionGuard } from "@/components/ProfileCompletionGuard";
import { AutoTranslator } from "@/components/AutoTranslator";
import { ScrollReveal } from "@/components/ScrollReveal";
import { ErrorReporter, reportFailure } from "@/components/ErrorReporter";
import { SITE_URL } from "@/lib/seo";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold">404</h1>
        <p className="mt-2 text-muted-foreground">This page doesn't exist.</p>
        <Link to="/" className="mt-6 inline-flex rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">Go home</Link>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  const router = useRouter();
  const [recovering, setRecovering] = React.useState(true);
  const caught = error instanceof Error ? error : new Error(String(error));
  console.error(caught);
  reportFailure({
    summary: caught.message || "Route render error",
    details: caught.stack,
    source: "route-boundary",
  });

  // Auto-recovery: most render failures are a transient data hiccup, so try one
  // silent reload before showing the user an error screen.
  React.useEffect(() => {
    const key = "bookswap_auto_recovered";
    let alreadyTried = true;
    try {
      alreadyTried = sessionStorage.getItem(key) === window.location.pathname;
      if (!alreadyTried) sessionStorage.setItem(key, window.location.pathname);
    } catch {
      alreadyTried = true;
    }
    if (alreadyTried) {
      setRecovering(false);
      return;
    }
    const t = setTimeout(() => {
      router.invalidate();
      reset();
      setRecovering(false);
    }, 1200);
    return () => clearTimeout(t);
  }, [error, reset, router]);

  if (recovering) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <p className="text-sm text-muted-foreground">Reconnecting…</p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold">Something went wrong</h1>
        <p className="mt-2 text-sm text-muted-foreground">An unexpected error occurred. Please try again.</p>
        <button
          onClick={() => { router.invalidate(); reset(); }}
          className="mt-6 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary/90"
        >Try again</button>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Book Swap — Exchange Books With Your Neighbors" },
      { name: "description", content: "Book Swap is a local peer-to-peer book exchange. Lend, borrow, and discover books from people near you." },
      { property: "og:title", content: "Book Swap — Exchange Books With Your Neighbors" },
      { name: "twitter:title", content: "Book Swap — Exchange Books With Your Neighbors" },
      { property: "og:description", content: "Book Swap is a local peer-to-peer book exchange. Lend, borrow, and discover books from people near you." },
      { name: "twitter:description", content: "Book Swap is a local peer-to-peer book exchange. Lend, borrow, and discover books from people near you." },
      { property: "og:image", content: `${SITE_URL}/og-image.png` },
      { name: "twitter:image", content: `${SITE_URL}/og-image.png` },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:type", content: "website" },
      { name: "google-site-verification", content: "GSMoNLXfkC1SD5ONiAJGqwj8glFNqaqEGvq1E3wKBaM" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "preload", as: "style", href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Cairo:wght@400;500;600;700;800&family=Tajawal:wght@400;500;700&display=swap" },
      { rel: "stylesheet", href: "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Cairo:wght@400;500;600;700;800&family=Tajawal:wght@400;500;700&display=swap" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  // Inline script sets lang/dir before React hydrates to prevent flash of wrong direction.
  const setLangScript = `(function(){try{var l=localStorage.getItem('bookswap_lang');if(l==='ar'){document.documentElement.lang='ar';document.documentElement.dir='rtl';}}catch(e){}})();`;
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
        <script dangerouslySetInnerHTML={{ __html: setLangScript }} />
      </head>
      <body>{children}<Scripts /></body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <I18nProvider>
          <AuthProvider>
            <Outlet />
            <ProfileCompletionGuard />
            <AutoTranslator />
            <ScrollReveal />
            <ErrorReporter />
            <FeedbackPrompt />
            <Toaster richColors position="top-right" />
          </AuthProvider>
        </I18nProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
